import { type GetStaticPaths, type GetStaticProps } from 'next'
import { useRouter } from 'next/router'
import React from 'react'
import { SWRConfig } from 'swr'

import Loader from '@/legacy-src/components/Loader'
import PublicTherapistProfileContainer from '@/legacy-src/containers/PublicTherapistProfileContainer'
import { type TherapistType } from '@/legacy-src/types/TherapistStateType'
import {
  fetchFromApi,
  fetchProcessedTherapists,
  fetchUseServices,
  fetchUseSpecializations,
} from '@/legacy-src/utilities/apiUtils'

import { fetchCMSMenu, getCMSMenuSWRKey } from '@/src/domains/cms-menus/services/fetchCMSMenu'
import {
  fetchHeaderServicesPages,
  getHeaderServicesPagesSWRKey,
} from '@/src/domains/header/services/fetchHeaderServicesPages'

import { USE_THERAPISTS_SWR_KEY } from '../hooks/useTherapists'

const TherapistPublicProfilePage = (props) => {
  const router = useRouter()

  // router.isFallback means user is requesting a page that is not yet generated
  if (router.isFallback) return <Loader visible loaderType="section-large" />

  const therapist: TherapistType | undefined = props.fallback[USE_THERAPISTS_SWR_KEY]?.find(
    (t) => t.slug === props.slug,
  )

  // this should be handled by getStaticProps, returning notFound: true
  // but for type safety we need to check if therapist is undefined
  if (!therapist) return <Loader visible loaderType="section-large" />

  return (
    <SWRConfig value={{ fallback: props.fallback }}>
      <PublicTherapistProfileContainer slug={props.slug} therapist={therapist} />
    </SWRConfig>
  )
}

export const therapistProfileGetStaticPaths = (async () => {
  const data = await fetchFromApi('therapist/list-open')
  const paths = data.filter((t) => t.slug && t.visible).map((t) => ({ params: { slug: t.slug } }))
  return { paths, fallback: true }
}) satisfies GetStaticPaths

export const therapistProfileGetStaticProps = (isPathValidForLocale: boolean) => {
  if (!isPathValidForLocale) return async () => ({ notFound: true })

  return (async ({ params }) => {
    const [
      useTherapists,
      useServices,
      useSpecializations,
      servicePages,
      useTherapistCategories,
      mainMenu,
      otherMenu,
      therapistMenu,
      clinicMenu,
    ] = await Promise.all([
      fetchProcessedTherapists(),
      fetchUseServices(),
      fetchUseSpecializations(),
      fetchHeaderServicesPages(),
      fetchFromApi('therapist/categories'),
      fetchCMSMenu('main-menu'),
      fetchCMSMenu('other-menu'),
      fetchCMSMenu('therapist-menu'),
      fetchCMSMenu('clinic-menu'),
    ])

    const therapistOnly = useTherapists.filter((t) => params?.slug && t.slug === params.slug)
    const isValidTherapist = therapistOnly.length > 0

    // revalidate needs to happen here, otherwise the 404 disappears only on new deploy
    // https://github.com/vercel/next.js/issues/21453
    if (!isValidTherapist) return { notFound: true, revalidate: 300 }

    return {
      props: {
        slug: params?.slug,
        fallback: {
          [USE_THERAPISTS_SWR_KEY]: therapistOnly,
          useServices,
          useSpecializations,
          useTherapistCategories,
          [getHeaderServicesPagesSWRKey()]: servicePages,
          [getCMSMenuSWRKey('main-menu')]: mainMenu,
          [getCMSMenuSWRKey('other-menu')]: otherMenu,
          [getCMSMenuSWRKey('therapist-menu')]: therapistMenu,
          [getCMSMenuSWRKey('clinic-menu')]: clinicMenu,
        },
      },
      revalidate: 300,
    }
  }) satisfies GetStaticProps
}

export default TherapistPublicProfilePage
